<script setup>
import { getCurrentInstance, inject, computed, onMounted, ref, watch } from 'vue';
import { useGroupStore } from '@base-shared/stores/group';
import CONST from '@/globals/constants'

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';

// Proxy is the instance of mixins defined in defineOptions
const { proxy } = getCurrentInstance();
const $utils = inject('$utils');
const $currentUser = inject('$currentUser');
const $signedInUserDetails = inject('$signedInUserDetails');
const $signedInUserAccounts = inject('$signedInUserAccounts');

defineOptions({
  mixins: [AuthMixin]
});

const groupStore = useGroupStore();

const isPageReady = ref(false);

const productName = computed(() => {
  if ($utils.isPageForLicenses()) {
    return CONST.ACTIVATE;
  }

  return CONFIG.PRODUCT_NAME;
});
const selectedAccountName = computed(() => {
  const selectedAccountGroupId = $currentUser.getUserGroupId();
  if (!selectedAccountGroupId || !$signedInUserAccounts.value?.length) {
    return '';
  }

  const account = $signedInUserAccounts.value.find(acc => acc.groupId === selectedAccountGroupId);
  return account ? account.name : '';
});

/**
 * Watches the `groupStore.groupInfo` for changes. Replacement
 * for the old `BIND_USER_INFO_IN_VUE` from EventBus.
 */
watch(
  () => groupStore.groupInfo,
  () => onUpdateEvent(),
  { deep: true }
);

async function redirectToHome () {
  const hasProductAccess = $currentUser.hasAccessToSelectedProduct();

  if (hasProductAccess) {
    try {
      await proxy.$router.push('/');
    } catch (error) {
      // Suppress thrown errors for duplicated and cancelled navigation
      console.error('Error redirecting to home:', error);
    }
  }
}

const onUpdateEvent = () => {
  isPageReady.value = true;
  redirectToHome();
};

onMounted(async () => {
  // Trigger auth-mixin to check if user is authenticated
  proxy.isAuthenticated();

  // Perform check on mount if user have access to the product
  await redirectToHome();

  // Check if user data is available
  if ($signedInUserDetails.value && $signedInUserAccounts.value) {
    isPageReady.value = true;
  }
});

</script>

<template>
  <div
    id="no-product-access-page-wrapper"
    data-component-element-id="no-product-access-page-wrapper"
    class="flex items-center justify-center w-full h-full"
  >
    <div id="menu-page" class="page">
      <div class="menu-heading">
        <p class="menu-title">{{productName}}</p>
        <p class="menu-caption">
          <template v-if="$utils.isPageForLicenses()">
            {{ $t('label.loginString4') }}
          </template>
          <template v-else>
            {{ $t('label.loginString3') }}
          </template>
        </p>
      </div>

      <div
        v-if="isPageReady"
        class="p-5"
      >
        <alert
          data-component-element-id="no-product-access-page-alert-banner"
          class="w-[706px] mt-28 px-5 py-5"
          type="error"
          size="medium"
          :hide-close-btn="true"
          :title="$t('texts.productAccessDisabled', { product: productName })"
          @close="onBannerClosed">
          <template v-slot:message>
            <span
              class="whitespace-pre-line"
              v-html="$t('texts.productAccessDisabledWarning', {
                product: `<strong>${productName}</strong>`,
                account: `<strong>${selectedAccountName}</strong>`,
              })"
            >
            </span>
          </template>
        </alert>
      </div>
    </div>
  </div>
</template>
